import { ASSISTANT_POPUP_SELECTOR } from "../constants";
import { IAssistantHighlight } from "../types";
import { findAssistantTarget, isAssistantTargetVisible } from "../utils";

export function findAssistantHighlightTargets(target: string): HTMLElement[] {
  const fields = Array.from(
    document.querySelectorAll<HTMLElement>(
      `[data-assistant-highlight-group~="${target}"]`
    )
  );

  if (fields.length) return fields.filter(isAssistantTargetVisible);

  const element = findAssistantTarget(target);

  return element ? [element] : [];
}

function getAssistantHighlightBorderRadius(element: HTMLElement): string {
  const bounds = element.getBoundingClientRect();
  const style = getComputedStyle(element);
  const corners = [
    parseFloat(style.borderTopLeftRadius),
    parseFloat(style.borderTopRightRadius),
    parseFloat(style.borderBottomRightRadius),
    parseFloat(style.borderBottomLeftRadius),
  ];

  for (
    let ancestor = element.parentElement;
    ancestor;
    ancestor = ancestor.parentElement
  ) {
    const clipping = getComputedStyle(ancestor);

    if (clipping.overflowX === "visible" && clipping.overflowY === "visible")
      continue;

    const parent = ancestor.getBoundingClientRect();
    const top = Math.abs(bounds.top - parent.top) < 1;
    const left = Math.abs(bounds.left - parent.left) < 1;
    const right = Math.abs(bounds.right - parent.right) < 1;
    const bottom = Math.abs(bounds.bottom - parent.bottom) < 1;

    if (top && left)
      corners[0] = Math.max(
        corners[0],
        parseFloat(clipping.borderTopLeftRadius)
      );

    if (top && right)
      corners[1] = Math.max(
        corners[1],
        parseFloat(clipping.borderTopRightRadius)
      );

    if (bottom && right)
      corners[2] = Math.max(
        corners[2],
        parseFloat(clipping.borderBottomRightRadius)
      );

    if (bottom && left)
      corners[3] = Math.max(
        corners[3],
        parseFloat(clipping.borderBottomLeftRadius)
      );
  }

  return corners.map((radius) => `${radius}px`).join(" ");
}

function getAssistantPopupHighlightLayer(
  element: HTMLElement
): number | undefined {
  const popup = element.closest<HTMLElement>(ASSISTANT_POPUP_SELECTOR);

  if (!popup) return undefined;

  let layer = 0;

  for (
    let ancestor: HTMLElement | null = popup;
    ancestor;
    ancestor = ancestor.parentElement
  ) {
    const value = Number.parseInt(getComputedStyle(ancestor).zIndex, 10);

    if (Number.isFinite(value)) layer = Math.max(layer, value);
  }

  return layer > 0 ? layer + 1 : undefined;
}

export function getAssistantVisibleHighlight(
  element: HTMLElement
): IAssistantHighlight | undefined {
  if (!isAssistantTargetVisible(element)) return undefined;

  const bounds = element.getBoundingClientRect();
  let top = Math.max(0, bounds.top);
  let left = Math.max(0, bounds.left);
  let bottom = Math.min(document.documentElement.clientHeight, bounds.bottom);
  let right = Math.min(document.documentElement.clientWidth, bounds.right);

  for (
    let ancestor = element.parentElement;
    ancestor;
    ancestor = ancestor.parentElement
  ) {
    const style = getComputedStyle(ancestor);
    const ancestorBounds = ancestor.getBoundingClientRect();
    const contentTop = ancestorBounds.top + ancestor.clientTop;
    const contentLeft = ancestorBounds.left + ancestor.clientLeft;

    if (style.overflowY !== "visible") {
      top = Math.max(top, contentTop);
      bottom = Math.min(bottom, contentTop + ancestor.clientHeight);
    }

    if (style.overflowX !== "visible") {
      left = Math.max(left, contentLeft);
      right = Math.min(right, contentLeft + ancestor.clientWidth);
    }
  }

  if (
    right <= left ||
    bottom <= top ||
    top > bounds.top ||
    left > bounds.left ||
    bottom < bounds.bottom ||
    right < bounds.right
  ) {
    return undefined;
  }

  const coveredByPopup = Array.from(
    document.querySelectorAll<HTMLElement>(ASSISTANT_POPUP_SELECTOR)
  ).some((popup) => {
    if (popup.contains(element)) return false;

    const popupBounds = popup.getBoundingClientRect();
    return (
      getComputedStyle(popup).visibility !== "hidden" &&
      popupBounds.width > 0 &&
      popupBounds.height > 0 &&
      popupBounds.left < bounds.right &&
      popupBounds.right > bounds.left &&
      popupBounds.top < bounds.bottom &&
      popupBounds.bottom > bounds.top
    );
  });

  if (coveredByPopup) return undefined;

  const contrastRing = element.matches(
    ".MuiButton-containedSecondary:not(.Mui-disabled), .MuiMenuItem-root:not(.Mui-disabled)"
  );

  return {
    top,
    left,
    width: right - left,
    height: bottom - top,
    contrastRing,
    zIndex: getAssistantPopupHighlightLayer(element),
    borderRadius: getAssistantHighlightBorderRadius(element),
  };
}
